// Виды кейса — он же упаковка (Иван, 25.10.2026, TASK-03). Названия, цены и подсветка
// приходят с сервера; здесь — только то, что видно на экране: картинка своего направления
// и строка описания, как в макете v59. Картинки — эскизы Codex, до фото напечатанных кейсов.
import type { CaseView, Direction } from "../api/types";

export const CASE_KINDS = ["crystal", "portal", "coffer"] as const;
export type CaseKind = (typeof CASE_KINDS)[number];

export function isCaseKind(value: string | null | undefined): value is CaseKind {
  return CASE_KINDS.includes(value as CaseKind);
}

type CaseLook = {
  description: string;
  // «В „Кристалле“ есть подсветка» — название вида в предложном падеже.
  inName: string;
  alt: Record<Direction, string>;
};

const CASE_LOOKS: Record<CaseKind, CaseLook> = {
  crystal: {
    description: "Полупрозрачный гранёный колпак на постаменте, фигурка видна сквозь него силуэтом.",
    inName: "Кристалле",
    alt: {
      miniature: "Кейс «Кристалл»: миниатюра под полупрозрачным гранёным колпаком на зелёном постаменте",
      gift: "Кейс «Кристалл»: фигурка под полупрозрачным гранёным колпаком, за ней светится кристалл",
    },
  },
  portal: {
    description: "Капсула открывается пополам, задняя половина остаётся нишей за фигуркой.",
    inName: "Портале",
    alt: {
      miniature: "Кейс «Портал»: зелёная капсула открыта пополам, миниатюра стоит в задней половине",
      gift: "Кейс «Портал»: красная капсула открыта пополам, фигурка в задней половине, рядом светится кристалл",
    },
  },
  coffer: {
    description: "Скруглённая раскладушка: внутри фигурка лежит, а дома встаёт на закрытую крышку, как на подиум.",
    inName: "Кофре",
    alt: {
      miniature: "Кейс «Кофр»: зелёная скруглённая раскладушка, миниатюра лежит внутри на боку",
      gift: "Кейс «Кофр»: красная скруглённая раскладушка, фигурка лежит внутри на боку",
    },
  },
};

export function caseDescription(kind: string): string {
  return isCaseKind(kind) ? CASE_LOOKS[kind].description : "";
}

export function caseImage(kind: string, direction: Direction): string {
  return `/showcase/case_${kind}_${direction === "gift" ? "gift" : "mini"}.jpg`;
}

export function caseImageAlt(kind: string, direction: Direction): string {
  return isCaseKind(kind) ? CASE_LOOKS[kind].alt[direction] : "";
}

// Так кейс назван в строке заказа: «кейс „Кристалл“».
export function caseLabel(title: string): string {
  return `кейс «${title}»`;
}

// То же с заглавной — отдельной строкой: в разбивке суммы и на статусе заказа.
export function caseRowTitle(title: string): string {
  return `Кейс «${title}»`;
}

// «В „Кристалле“ и „Портале“ есть подсветка» — по видам, где она есть. null — ни у одного.
export function lightNote(cases: CaseView[]): string | null {
  const names = cases
    .filter((caseView) => caseView.light && isCaseKind(caseView.kind))
    .map((caseView) => `«${CASE_LOOKS[caseView.kind as CaseKind].inName}»`);
  if (names.length === 0) return null;
  const listed = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} и ${names.at(-1)}`;
  return `В ${listed} есть подсветка`;
}
